import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export interface ClientSummary {
  key: string;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  date: string;
  source: 'shop' | 'credit';
  detailPath: string;
}

interface Props {
  client: ClientSummary;
  /** Identifiant brut (id boutique ou id du carnet de crédit). */
  rawId: string;
  onClose: () => void;
}

/**
 * ClientDetailModal — fiche client simple et lisible.
 * Le clic sur un client n'ouvre plus systématiquement l'application Zifek
 * Crédit : on affiche d'abord ses informations, puis on propose explicitement
 * d'approfondir (fiche complète ou carnet de crédit) si besoin.
 */
export default function ClientDetailModal({ client, rawId, onClose }: Props) {
  const navigate = useNavigate();
  const [balance, setBalance] = useState<number | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  useEffect(() => {
    if (client.source !== 'credit') return;
    let cancelled = false;
    setLoadingBalance(true);
    supabase
      .from('credit_transactions')
      .select('type, amount')
      .eq('customer_id', Number(rawId))
      .then(({ data }) => {
        if (cancelled) return;
        const rows = (data || []) as { type: string; amount: number | string }[];
        const total = rows.reduce((sum, r) => sum + (r.type === 'payment' ? -1 : 1) * (Number(r.amount) || 0), 0);
        setBalance(total);
        setLoadingBalance(false);
      });
    return () => { cancelled = true; };
  }, [client.source, rawId]);

  const waNumber = client.phone ? client.phone.replace(/[^0-9+]/g, '') : '';
  const created = new Date(client.date).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative w-full sm:max-w-md bg-background-50 rounded-t-2xl sm:rounded-xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-background-200/70">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
              <span className="text-accent-700 font-bold">{client.name.charAt(0).toUpperCase()}</span>
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold font-heading text-foreground-950 truncate">{client.name}</h3>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${
                  client.source === 'credit'
                    ? 'bg-primary-50 text-primary-700'
                    : 'bg-secondary-100 text-secondary-900'
                }`}>
                  <i className={client.source === 'credit' ? 'ri-book-2-line' : 'ri-store-2-line'}></i>
                  {client.source === 'credit' ? 'Carnet crédit' : 'Boutique'}
                </span>
                <span className="text-[10px] text-foreground-400">Client depuis le {created}</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          <dl className="space-y-3">
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-line"></i>
              </span>
              <div className="min-w-0">
                <dt className="text-[11px] text-foreground-500">Email</dt>
                <dd className="text-sm text-foreground-900 break-all">{client.email || '—'}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-line"></i>
              </span>
              <div className="min-w-0">
                <dt className="text-[11px] text-foreground-500">Téléphone</dt>
                <dd className="text-sm text-foreground-900">{client.phone || '—'}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-map-pin-line"></i>
              </span>
              <div className="min-w-0">
                <dt className="text-[11px] text-foreground-500">Adresse</dt>
                <dd className="text-sm text-foreground-900">{client.address || '—'}</dd>
              </div>
            </div>
            {client.source === 'credit' && (
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-wallet-3-line"></i>
                </span>
                <div className="min-w-0">
                  <dt className="text-[11px] text-foreground-500">Solde du carnet</dt>
                  <dd className="text-sm font-semibold text-foreground-900">
                    {loadingBalance ? (
                      <i className="ri-loader-4-line animate-spin text-foreground-400"></i>
                    ) : balance == null ? (
                      '—'
                    ) : (
                      `${balance.toLocaleString('fr-FR')}`
                    )}
                  </dd>
                </div>
              </div>
            )}
          </dl>

          <div className="mt-5 space-y-2">
            {client.phone && (
              <a
                href={`https://wa.me/${waNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors no-underline whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i>
                Écrire sur WhatsApp
              </a>
            )}
            {client.email && (
              <a
                href={`mailto:${client.email}`}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full bg-background-100 border border-background-200/70 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors no-underline whitespace-nowrap"
              >
                <i className="ri-mail-send-line"></i>
                Envoyer un email
              </a>
            )}
            <button
              type="button"
              onClick={() => { onClose(); navigate(client.detailPath); }}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full bg-secondary-100 text-secondary-900 text-sm font-medium hover:bg-secondary-200/70 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={client.source === 'credit' ? 'ri-book-2-line' : 'ri-file-list-3-line'}></i>
              {client.source === 'credit' ? 'Ouvrir le carnet de crédit' : 'Ouvrir la fiche détaillée'}
            </button>
          </div>

          <p className="mt-4 text-[11px] text-foreground-400 leading-relaxed">
            {client.source === 'credit'
              ? "Ce client vient du carnet de crédit. La fiche détaillée de l'application Zifek Crédit reste accessible ci-dessus, mais elle n'est plus ouverte automatiquement."
              : 'La fiche détaillée regroupe ses commandes, visites, panier, fidélité et finances.'}
          </p>
        </div>
      </div>
    </div>
  );
}